import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import PhoneInput from './PhoneInput';
import { getGeoLocation, submitLead, getDialCodeFromCountryCode } from '../services/api';

const ContactPopup = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [errors, setErrors] = useState({
    email: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [geoData, setGeoData] = useState(null);
  const [countryDialCode, setCountryDialCode] = useState('1');
  const [defaultCountryCode, setDefaultCountryCode] = useState('+1');

  // Fetch geolocation on mount
  useEffect(() => {
    const fetchGeoLocation = async () => {
      const data = await getGeoLocation();
      if (data && data.isSuccess) {
        setGeoData(data);
        const dialCode = getDialCodeFromCountryCode(data.data.countryCode);
        setCountryDialCode(dialCode.toString());
        setDefaultCountryCode(`+${dialCode}`);
      }
    };
    fetchGeoLocation();
  }, []);

  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      return 'Email is required';
    }
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address (e.g., user@example.com)';
    }
    return '';
  };

  // Phone validation
  const validatePhone = (phone) => {
    // Remove all non-digit characters for validation
    const digitsOnly = phone.replace(/\D/g, '');
    
    if (!phone) {
      return 'Phone number is required';
    }
    if (digitsOnly.length !== 10) {
      return 'Phone number must be exactly 10 digits after country code';
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate email and phone
    const emailError = validateEmail(formData.email);
    const phoneError = validatePhone(formData.phone);
    
    setErrors({
      email: emailError,
      phone: phoneError
    });

    // If there are validation errors, don't submit
    if (emailError || phoneError) {
      toast.error('Please fix the errors before submitting');
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare lead data
      const leadPayload = {
        name: formData.name,
        email: formData.email,
        countryDialCode: parseInt(countryDialCode),
        country: geoData?.data?.countryCode || 'US',
        phoneNumber: formData.phone, // Already numeric only from validation
        ipAddress: geoData?.data?.ipAddress || '',
        description: formData.message || 'Contact form submission',
        pageUrl: window.location.href,
        city: geoData?.data?.cityName || '',
        state: geoData?.data?.regionName || '',
        geoLocation: geoData ? JSON.stringify(geoData) : '',
        packageTitle: '',
        packageAmount: 0
      };

      // Submit lead
      const response = await submitLead(leadPayload);

      console.log('API Response:', response);

      if (response.statusCode === 200 && response.data?.data?.customerId) {
        // Success - redirect to application
        toast.success(response.data.data.message || 'Thank you! We will contact you soon.');
        setTimeout(() => {
          window.location.href = `/application/main?customerid=${response.data.data.customerId}`;
        }, 1500);
      } else if (response.statusCode === 401) {
        // Unauthorized
        toast.error(response.data?.message || 'Authorization error');
      } else if (response.statusCode === 400) {
        // Bad request
        toast.error(response.data?.errorMessage || response.message || 'Invalid data provided');
      } else {
        // Other errors
        toast.error(response.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      toast.error('An unexpected error occurred. Please try again.');
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear error when user starts typing
    if (name === 'email') {
      setErrors(prev => ({ ...prev, email: '' }));
    }
  };

  const handlePhoneChange = (e) => {
    setFormData({ ...formData, phone: e.target.value });
    // Clear error when user starts typing
    setErrors(prev => ({ ...prev, phone: '' }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white border-gray-200 text-slate-900 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-slate-900">Get in Touch</DialogTitle>
          <DialogDescription className="text-gray-600">
            Fill out the form below and we'll get back to you within 24 hours.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label htmlFor="popup-name" className="text-slate-700 font-medium">Name *</Label>
            <Input
              id="popup-name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="bg-white border-gray-300 text-slate-900 placeholder:text-gray-400 focus:border-amber-500 focus:ring-amber-500 mt-2"
              placeholder="Your name"
            />
          </div>

          <div>
            <Label htmlFor="popup-email" className="text-slate-700 font-medium">Email *</Label>
            <Input
              id="popup-email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              className={`bg-white border-gray-300 text-slate-900 placeholder:text-gray-400 focus:border-amber-500 focus:ring-amber-500 mt-2 ${errors.email ? 'border-red-500' : ''}`}
              placeholder="your@email.com"
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email}</p>
            )}
          </div>

          <div>
            <PhoneInput
              id="popup-phone"
              value={formData.phone}
              onChange={handlePhoneChange}
              onCountryChange={(code) => setCountryDialCode(code)}
              defaultCountryCode={defaultCountryCode}
              required
              className="bg-white border-gray-300 text-slate-900 placeholder:text-gray-400 focus:border-amber-500 focus:ring-amber-500"
              error={errors.phone}
            />
            {errors.phone && (
              <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
            )}
          </div>

          <div>
            <Label htmlFor="popup-message" className="text-slate-700 font-medium">Message *</Label>
            <Textarea
              id="popup-message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows={4}
              className="bg-white border-gray-300 text-slate-900 placeholder:text-gray-400 focus:border-amber-500 focus:ring-amber-500 mt-2"
              placeholder="Tell us about your trademark needs..."
            />
          </div>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold shadow-lg"
          >
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ContactPopup;
